/**
 * Ad-spend hooks with mock fallback. Client names are resolved from the clients
 * cache because the ad-spend list endpoint returns only `clientId`.
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AD_SPEND_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { AdSpend } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { useClients } from "@/features/clients/clients.hooks";
import { adSpendApi } from "./adspend.api";
import { toAdSpend, toAdSpendWriteBody, toMockAdSpend, type AdSpendInput } from "./adspend.adapter";
import { useAdSpendStore } from "./adspend.store";

const LIST_PARAMS = { limit: 100, sortBy: "date", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseAdSpendResult {
  adSpend: AdSpend[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useAdSpend(): UseAdSpendResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useAdSpendStore((s) => s.active);
  const { clients } = useClients();
  const nameById = useMemo(() => new Map(clients.map((c) => [c.id, c.name])), [clients]);

  const query = useQuery({
    queryKey: queryKeys.adSpendList(LIST_PARAMS),
    queryFn: () => adSpendApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) return { adSpend: mockActive, isLoading: false, isError: false, isFetching: false, source: "mock", error: null, refetch: query.refetch };
  if (query.data) return { adSpend: query.data.data.map((a) => toAdSpend(a, nameById)), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { adSpend: mockActive, isLoading: false, isError: true, isFetching: false, source: "mock", error: query.error, refetch: query.refetch };
  return { adSpend: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

function useInvalidateAdSpend() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: AD_SPEND_QUERY_PREFIX });
}

export function useCreateAdSpend() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const add = useAdSpendStore((s) => s.add);
  const invalidate = useInvalidateAdSpend();
  return useMutation({
    mutationFn: async (input: AdSpendInput) => {
      if (authed) return adSpendApi.create(toAdSpendWriteBody(input));
      const mock = toMockAdSpend(input);
      add(mock);
      return mock;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useArchiveAdSpend() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const archive = useAdSpendStore((s) => s.archive);
  const invalidate = useInvalidateAdSpend();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return adSpendApi.archive(id);
      archive(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useRestoreAdSpend() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const restore = useAdSpendStore((s) => s.restore);
  const invalidate = useInvalidateAdSpend();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return adSpendApi.restore(id);
      restore(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}
